// ABOUTME: Utility functions for email verification enforcement
// ABOUTME: Calculates days remaining, checks if grace period expired, determines if user should be blocked

import { User } from '../types/api';

/**
 * Calculate days remaining in email verification grace period
 * @param createdAt ISO 8601 date string of account creation
 * @param familyTimezone Family timezone (e.g., "America/New_York")
 * @returns Number of days remaining (1-7), or 0 if grace period expired
 */
export function calculateDaysRemaining(
  createdAt: string | undefined,
  familyTimezone?: string
): number {
  if (!createdAt) return 0;

  const timezone = familyTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Parse creation date as UTC
  const creationDate = new Date(createdAt);

  // Get current date
  const now = new Date();

  // Format both dates to the target timezone to get the date part
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  // Get the formatted date parts
  const currentParts = formatter.formatToParts(now);
  const creationParts = formatter.formatToParts(creationDate);

  // Extract year, month, day
  const getCurrentPart = (type: string) => currentParts.find(p => p.type === type)?.value || '0';
  const getCreationPart = (type: string) => creationParts.find(p => p.type === type)?.value || '0';

  // Create midnight dates in the same timezone for comparison
  const currentMidnight = new Date(
    parseInt(getCurrentPart('year')),
    parseInt(getCurrentPart('month')) - 1,
    parseInt(getCurrentPart('day'))
  );

  const creationMidnight = new Date(
    parseInt(getCreationPart('year')),
    parseInt(getCreationPart('month')) - 1,
    parseInt(getCreationPart('day'))
  );

  // Calculate days elapsed
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysElapsed = Math.floor(
    (currentMidnight.getTime() - creationMidnight.getTime()) / msPerDay
  );

  // Creation day = 7 days remaining, day after = 6 remaining, etc.
  const daysRemaining = 7 - daysElapsed;
  return Math.max(0, daysRemaining);
}

/**
 * Check if user's grace period has expired
 * @param user User object from AuthContext
 * @param familyTimezone Family timezone
 * @returns true if grace period expired (day 8+)
 */
export function isGracePeriodExpired(
  user: User | null,
  familyTimezone?: string
): boolean {
  if (!user || !user.createdAt) return false;
  if (user.emailVerificationStatus === 'Verified') return false;
  if (user.emailVerificationStatus === 'VerificationSkipped') return false;

  const daysRemaining = calculateDaysRemaining(user.createdAt, familyTimezone);
  return daysRemaining === 0;
}

/**
 * Check if user should see the blocking modal
 * @param user User object from AuthContext
 * @param familyTimezone Family timezone
 * @param requireEmailVerification Site-wide setting (default true)
 * @returns true if modal should be displayed
 */
export function shouldShowBlockingModal(
  user: User | null,
  familyTimezone?: string,
  requireEmailVerification: boolean = true
): boolean {
  if (!user) return false;
  if (!requireEmailVerification) return false; // Site-wide disabled
  if (user.emailVerificationStatus === 'Verified') return false;
  if (user.emailVerificationStatus === 'VerificationSkipped') return false;
  if (user.isAdmin) return false; // Admins not blocked

  return isGracePeriodExpired(user, familyTimezone);
}

/**
 * Check if user should see the verification ribbon
 * @param user User object from AuthContext
 * @param requireEmailVerification Site-wide setting (default true)
 * @returns true if ribbon should be displayed
 */
export function shouldShowVerificationRibbon(
  user: User | null,
  requireEmailVerification: boolean = true
): boolean {
  if (!user) return false;
  if (!requireEmailVerification) return false; // Site-wide disabled
  if (user.emailVerificationStatus === 'Verified') return false;
  if (user.emailVerificationStatus === 'VerificationSkipped') return false;

  const daysRemaining = calculateDaysRemaining(user.createdAt);
  return daysRemaining > 0;
}
